import { supabase } from "../integrations/supabase/client"
import type { TableItem } from "@/types/domain"
import { logErrorToConsole, translateSupabaseError } from "../utils/errorHandlerUtils"
import type { Database } from "../types/supabase"
import type { PostgrestError } from "@supabase/supabase-js"
import type { ActionResponse } from "../lib/actions/types"
export type { ActionResponse } from "../lib/actions/types"

type TableName = keyof Database['public']['Tables']
type RpcFunctionName = keyof Database['public']['Functions']

/**
 * Normaliza el id del registro retornado por Supabase a string y lo retorna como R.
 */
function normalizeRecord<R extends TableItem>(data: unknown): R {
  if (data && typeof data === 'object' && 'id' in data) {
    const typedData = data as Record<string, unknown> & { id: unknown };
    return { ...typedData, id: String(typedData.id) } as R;
  }
  return data as R;
}

/**
 * Error personalizado para operaciones en la API
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: PostgrestError | Error | unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Transforma un error de Supabase en un objeto de error estándar
 */
function handleSupabaseError(error: PostgrestError | Error | unknown, operacion: string, entidad: string): { message: string; code?: string } {
  logErrorToConsole(error, {
    module: 'baseApi',
    operation: operacion,
    details: { entity: entidad }
  });

  // Toda la traducción vive en translateSupabaseError (códigos Postgres/
  // PostgREST, constraints CHECK, passthrough de los RAISE en español de los
  // RPCs, patrones de red/sesión). Aquí solo se aporta el fallback contextual.
  const { message, code } = translateSupabaseError(
    error,
    `No se pudo ${operacion} (${entidad}). Intenta de nuevo.`
  );
  return { message, code: code ?? 'UNKNOWN_ERROR' };
}

/**
 * Interfaz para opciones de filtrado
 */
export interface FilterOptions {
  column: string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'in' | 'is';
  value: unknown;
}

type FilterOperator = FilterOptions['operator'];

/**
 * Obtiene todos los registros de una tabla con soporte para filtros complejos
 */
export async function fetchTable<T extends TableItem>(
  table: TableName, 
  options?: {
    select?: string;
    orderBy?: string | Array<{ column: string; ascending?: boolean }>;
    ascending?: boolean;
    filters?: FilterOptions[];
    limit?: number;
    offset?: number;
  }
): Promise<ActionResponse<T[]>> {
  try {
    let query = supabase.from(table).select(options?.select || "*")
    
    // Aplicar filtros
    if (options?.filters) {
      const allowedOperators: FilterOperator[] = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'like', 'ilike', 'in', 'is'];
      for (const filter of options.filters) {
        // Validar el operador antes de indexar la query a ciegas
        if (!allowedOperators.includes(filter.operator)) {
          logErrorToConsole(new Error(`Operador de filtro no permitido: ${String(filter.operator)}`), {
            module: 'baseApi',
            operation: 'fetchTable',
            details: { entity: table, column: filter.column },
          });
          continue;
        }
        // Type assertion needed due to Supabase's type definitions
        const op = filter.operator;
        query = (query as unknown as Record<string, (column: string, value: unknown) => typeof query>)[op](filter.column, filter.value)
      }
    }
    
    // Aplicar ordenamiento
    if (options?.orderBy) {
      if (typeof options.orderBy === 'string') {
        query = query.order(options.orderBy, { ascending: options.ascending ?? true })
      } else if (Array.isArray(options.orderBy)) {
        for (const order of options.orderBy) {
          query = query.order(order.column, { ascending: order.ascending ?? true })
        }
      }
    }
    
    // Aplicar paginación
    if (options?.limit) {
      query = query.limit(options.limit)
    }
    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1)
    }
    
    const { data, error } = await query
    
    if (error) {
      return {
        status: 'error',
        error: handleSupabaseError(error, "obtener", table)
      };
    }
    
    return {
      status: 'success',
      // ?? para no confundir un array vacío válido con null.
      // Cast intencional: las definiciones de Supabase no infieren T[] aquí.
      data: (data ?? []) as unknown as T[]
    };
  } catch (error) {
    return {
      status: 'error',
      error: handleSupabaseError(error, "obtener", table)
    };
  }
}

/**
 * Obtiene un registro específico por ID
 */
export async function fetchItem<T extends TableItem>(
  table: TableName, 
  id: string, 
  select = "*"
): Promise<ActionResponse<T | null>> {
  try {
    const { data, error } = await supabase
      .from(table)
      .select(select)
      // `table` es dinámico; este helper solo se usa con tablas que poseen id.
      .eq("id" as never, id)
      .single()
    
    // PGRST116 = el registro no existe; no es un error, sólo "no encontrado".
    if (error && error.code === 'PGRST116') {
      return {
        status: 'success',
        data: null
      };
    }

    if (error) {
      return {
        status: 'error',
        error: handleSupabaseError(error, "obtener", table)
      };
    }

    const result = data ? (data as unknown as T) : null;
    return {
      status: 'success',
      data: result
    };
  } catch (error) {
    return {
      status: 'error',
      error: handleSupabaseError(error, "obtener", table)
    };
  }
}

/**
 * Crea un nuevo registro
 */
export async function createItem<T extends Omit<TableItem, "id">, R extends TableItem>(
  table: TableName, 
  item: T,
  select = "*"
): Promise<ActionResponse<R>> {
  try {
    const { data, error } = await supabase
      .from(table)
      // La correlación tabla→Insert se valida en los servicios concretos; aquí
      // se pierde al aceptar un nombre de tabla dinámico.
      .insert([item] as never)
      .select(select)
      .single()
    
    if (error) {
      return {
        status: 'error',
        error: handleSupabaseError(error, "crear", table)
      };
    }
    
    if (!data) {
      return {
        status: 'error',
        error: { message: `No se pudo crear el elemento en ${table}`, code: 'CREATE_FAILED' }
      };
    }
    
    return {
      status: 'success',
      data: normalizeRecord<R>(data)
    };
  } catch (error) {
    return {
      status: 'error',
      error: handleSupabaseError(error, "crear", table)
    };
  }
}

/**
 * Actualiza un registro existente
 */
export async function updateItem<T extends Partial<TableItem>, R extends TableItem>(
  table: TableName, 
  id: string, 
  updates: T,
  select = "*"
): Promise<ActionResponse<R>> {
  try {
    const { data, error } = await supabase
      .from(table)
      .update(updates as never)
      .eq("id" as never, id)
      .select(select)
      .single()
    
    if (error) {
      return {
        status: 'error',
        error: handleSupabaseError(error, "actualizar", table)
      };
    }
    
    if (!data) {
      return {
        status: 'error',
        error: { message: `No se encontró el elemento ${id} en ${table} después de actualizar`, code: 'NOT_FOUND' }
      };
    }
    
    return {
      status: 'success',
      data: normalizeRecord<R>(data)
    };
  } catch (error) {
    return {
      status: 'error',
      error: handleSupabaseError(error, "actualizar", table)
    };
  }
}

/**
 * Elimina un registro
 */
export async function deleteItem(
  table: TableName, 
  id: string
): Promise<ActionResponse<void>> {
  try {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq("id" as never, id)
    
    if (error) {
      return {
        status: 'error',
        error: handleSupabaseError(error, "eliminar", table)
      };
    }
    
    return {
      status: 'success'
    };
  } catch (error) {
    return {
      status: 'error',
      error: handleSupabaseError(error, "eliminar", table)
    };
  }
}

/**
 * Ejecuta una función RPC en Supabase
 */
export async function executeRpc<T = unknown>(
  functionName: RpcFunctionName,
  params?: Record<string, unknown>
): Promise<ActionResponse<T>> {
  try {
    // Cast params to satisfy Supabase's strict RPC argument types
    const { data, error } = await supabase.rpc(functionName, params as Parameters<typeof supabase.rpc>[1])
    
    if (error) {
      return {
        status: 'error',
        error: handleSupabaseError(error, "ejecutar función", functionName)
      };
    }
    
    return {
      status: 'success',
      data: data as T
    };
  } catch (error) {
    return {
      status: 'error',
      error: handleSupabaseError(error, "ejecutar función", functionName)
    };
  }
}

